import {
  Button,
  Input,
  RichTextEditor,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useRef, useState } from "react";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cms } from "../../api/cms.service";
import { PageHeader } from "../../components/PageHeader";
import { ROUTES } from "../../routes";
import type { CmsPage } from "../../types/cms";
import { PageAddressPreview } from "./components/PageAddressFields/PageAddressPreview";
import { PageSectionField } from "./components/PageAddressFields/PageSectionField";
import { ParentPageField } from "./components/PageAddressFields/ParentPageField";
import { SectionHomeAddress } from "./components/PageAddressFields/SectionHomeAddress";
import { PageLayoutField } from "./components/PageLayoutField/PageLayoutField";
import { ListInSidebarField } from "./components/SidebarField/ListInSidebarField";
import { SidebarField } from "./components/SidebarField/SidebarField";
import { getLayoutOptions, getPageType, isLayoutOfferedInSection } from "./pageTypeRegistry";
import {
  firstInvalidField,
  LAYOUT_REQUIRED_MESSAGE,
  validatePageTitle,
  validateSidebar,
} from "./pageLayoutValidation";
import {
  NO_SIDEBAR_SELECTION,
  type SidebarSelection,
  sidebarListingForSave,
  sidebarOptionsInSection,
  toSidebarRequest,
} from "./pageSidebar";
import { useSidebarOptions } from "./useSidebarOptions";
import {
  isSlugConflict,
  parentPageChoices,
  sectionHomeOf,
  SEGMENT_HINT,
  SLUG_TAKEN_MESSAGE,
  slugDescribedBy,
  validateSegment,
} from "./pageAddress";
import {
  DEFAULT_PAGE_SECTION,
  isPageSection,
  type PageSection,
  sectionAllowsHome,
} from "./pageSection";
import { useCmsPages } from "./useCmsPages";
import { pageOptionsFromFormValues, type PageMetadataFormValues } from "./pageMetadata";
import styles from "./CMS.module.css";

type CreatePageFormValues = PageMetadataFormValues & {
  title: string;
  slug: string;
  section: PageSection;
  sectionHome: boolean;
  parentId: string;
  type: string;
  status: CmsPage["status"];
  sidebar: SidebarSelection;
  listInSidebar: boolean;
  content: string;
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function PageNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedSection = searchParams.get("section");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const { cmsPages, cmsPagesStatus } = useCmsPages();
  const { sidebarOptions, sidebarOptionsStatus } = useSidebarOptions(cmsPages, cmsPagesStatus);

  // Track whether the user has manually edited the slug
  const slugManuallyEdited = useRef(false);
  const layoutTouched = useRef(false);
  const sidebarTouched = useRef(false);
  const listingTouched = useRef(false);
  const layoutPreset = useRef(false);
  const sidebarPreset = useRef(false);
  const listingPreset = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setFocus,
    setError,
    formState: { errors, isSubmitted },
  } = useForm<CreatePageFormValues>({
    shouldFocusError: false,
    defaultValues: {
      title: "",
      slug: "",
      sectionHome: false,
      section: isPageSection(requestedSection) ? requestedSection : DEFAULT_PAGE_SECTION,
      parentId: "",
      type: "",
      status: "draft",
      sidebar: NO_SIDEBAR_SELECTION,
      listInSidebar: true,
      content: "",
      metaTitle: "",
      metaDescription: "",
    },
  });

  const titleValue = watch("title");
  const slugValue = watch("slug");
  const sectionValue = watch("section");
  const parentIdValue = watch("parentId");
  const sectionHomeValue = watch("sectionHome");
  const parentChoices = parentPageChoices(cmsPages, null, sectionValue);
  const typeValue = watch("type");
  const showMenuField = getPageType(typeValue)?.hasSubmenu === true;

  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setValue("title", value, { shouldValidate: true, shouldDirty: true });
    // Auto-generate slug from title if slug hasn't been manually edited
    if (!slugManuallyEdited.current || slugValue === slugify(titleValue)) {
      setValue("slug", slugify(value), { shouldValidate: true, shouldDirty: true });
    }
  }

  function handleSlugChange(e: React.ChangeEvent<HTMLInputElement>) {
    slugManuallyEdited.current = true;
    setValue("slug", e.target.value, { shouldValidate: true, shouldDirty: true });
  }

  function focusFirstInvalidField(invalidFields: FieldErrors<CreatePageFormValues>) {
    const field = firstInvalidField(invalidFields);
    if (field) {
      setFocus(field);
    }
  }

  function applyParentDefaults(parentId: string) {
    const parent = cmsPages.find(page => page.id === parentId);
    const parentMenuId = parent && getPageType(parent.type)?.hasSubmenu ? parent.menuId : null;
    const setOptions = { shouldDirty: true, shouldValidate: isSubmitted };

    if (!layoutTouched.current) {
      if (parent) {
        setValue("type", parentMenuId ? "page_with_submenu" : "simple_page", setOptions);
        layoutPreset.current = true;
      } else if (layoutPreset.current) {
        setValue("type", "", setOptions);
        layoutPreset.current = false;
      }
    }

    if (!sidebarTouched.current) {
      if (parentMenuId) {
        setValue(
          "sidebar",
          { ...NO_SIDEBAR_SELECTION, choice: "join", menuId: parentMenuId },
          setOptions,
        );
        sidebarPreset.current = true;
      } else if (sidebarPreset.current) {
        setValue("sidebar", NO_SIDEBAR_SELECTION, setOptions);
        sidebarPreset.current = false;
      }
    }

    if (!listingTouched.current && (parentMenuId || listingPreset.current)) {
      setValue("listInSidebar", true, setOptions);
      listingPreset.current = Boolean(parentMenuId);
    }
  }

  async function onSubmit(data: CreatePageFormValues) {
    const pageType = getPageType(data.type);

    setSaving(true);
    try {
      const page = await cms.createPage({
        title: data.title.trim(),
        section: data.section,
        ...(data.sectionHome
          ? { sectionHome: true }
          : { slug: data.slug.trim(), parentId: data.parentId || null }),
        type: data.type,
        status: data.status,
        ...(pageType?.hasSubmenu
          ? { sidebar: toSidebarRequest(data.sidebar, data.title) }
          : { menuId: null }),
        content: data.content || null,
        ...sidebarListingForSave(data.type, data.listInSidebar, pageOptionsFromFormValues(data)),
      });
      setToast({ message: "Page created successfully", type: "success" });
      navigate(ROUTES.cms.pageDetail(page.id));
    } catch (err) {
      if (isSlugConflict(err) && !data.sectionHome) {
        setError("slug", { type: "server", message: SLUG_TAKEN_MESSAGE }, { shouldFocus: true });
        return;
      }
      const message = err instanceof Error ? err.message : "Unknown error";
      setToast({ message: `Failed to create page: ${message}`, type: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="New CMS Page">
        <Button variant="outlined" color="default" onClick={() => navigate(ROUTES.cms.pages)}>
          Back to Pages
        </Button>
      </PageHeader>

      <div className={styles.detailWrapper}>
        <form onSubmit={handleSubmit(onSubmit, focusFirstInvalidField)}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Page Details</h2>

            <div className={styles.fieldGrid}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="page-title">
                  Title <span className={styles.required}>*</span>
                </label>
                <Input
                  id="page-title"
                  {...register("title", { validate: validatePageTitle })}
                  onChange={handleTitleChange}
                  placeholder="Page title"
                  error={errors.title?.message}
                />
              </div>

              <Controller
                name="section"
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <PageSectionField
                    id="page-section"
                    value={value}
                    onChange={section => {
                      onChange(section);
                      if (parentIdValue) {
                        setValue("parentId", "", { shouldDirty: true });
                        applyParentDefaults("");
                      }
                      if (getValues("sidebar").choice === "join") {
                        setValue("sidebar", NO_SIDEBAR_SELECTION, { shouldDirty: true });
                      }
                      if (!sectionAllowsHome(section) || sectionHomeOf(cmsPages, section, null)) {
                        setValue("sectionHome", false);
                      }
                      if (!isLayoutOfferedInSection(getValues("type"), section)) {
                        setValue("type", "", { shouldDirty: true });
                      }
                    }}
                    sectionHome={sectionHomeValue}
                    onSectionHomeChange={sectionHome =>
                      setValue("sectionHome", sectionHome, { shouldDirty: true })
                    }
                    existingHome={sectionHomeOf(cmsPages, value, null)}
                    inputRef={ref}
                  />
                )}
              />

              {sectionHomeValue ? (
                <SectionHomeAddress id="page-slug" section={sectionValue} />
              ) : (
                <>
                  <Controller
                    name="parentId"
                    control={control}
                    render={({ field: { value, onChange, ref } }) => (
                      <ParentPageField
                        id="page-parent"
                        section={sectionValue}
                        pages={parentChoices}
                        pagesStatus={cmsPagesStatus}
                        value={value}
                        savedParentPath={null}
                        onChange={parentId => {
                          onChange(parentId);
                          applyParentDefaults(parentId);
                        }}
                        inputRef={ref}
                      />
                    )}
                  />

                  <div className={styles.formField}>
                    <label className={styles.formLabel} htmlFor="page-slug">
                      Slug <span className={styles.required}>*</span>
                    </label>
                    <Input
                      id="page-slug"
                      {...register("slug", {
                        validate: (value, values) =>
                          values.sectionHome
                            ? true
                            : validateSegment(value, {
                                section: values.section,
                                pages: cmsPagesStatus === "ready" ? cmsPages : null,
                                editedPageId: null,
                              }),
                      })}
                      onChange={handleSlugChange}
                      placeholder="cookies-policy"
                      error={errors.slug?.message}
                      aria-describedby={slugDescribedBy("page-slug", Boolean(errors.slug))}
                    />
                    <span id="page-slug-hint" className={styles.fieldHint}>
                      {SEGMENT_HINT}
                    </span>
                    <PageAddressPreview
                      id="page-slug-address"
                      section={sectionValue}
                      parentSlug={
                        parentChoices.find(page => page.id === parentIdValue)?.slug ?? null
                      }
                      segment={slugValue}
                    />
                  </div>
                </>
              )}

              <div className={styles.fieldFull}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: LAYOUT_REQUIRED_MESSAGE }}
                  render={({ field: { value, onChange, ref } }) => (
                    <PageLayoutField
                      name="page-layout"
                      value={value}
                      options={getLayoutOptions(value, sectionValue)}
                      onChange={layout => {
                        layoutTouched.current = true;
                        onChange(layout);
                      }}
                      error={errors.type?.message}
                      inputRef={ref}
                    />
                  )}
                />
              </div>

              {showMenuField && (
                <div className={styles.fieldFull}>
                  <Controller
                    name="sidebar"
                    control={control}
                    rules={{ validate: validateSidebar }}
                    render={({ field: { value, onChange, ref } }) => (
                      <SidebarField
                        name="page-sidebar"
                        options={sidebarOptionsInSection(sidebarOptions, sectionValue)}
                        optionsStatus={sidebarOptionsStatus}
                        value={value}
                        onChange={selection => {
                          sidebarTouched.current = true;
                          onChange(selection);
                        }}
                        pageTitle={titleValue}
                        renameMenuId={null}
                        error={errors.sidebar?.message}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Controller
                    name="listInSidebar"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <ListInSidebarField
                        id="page-list-in-sidebar"
                        checked={value}
                        onChange={checked => {
                          listingTouched.current = true;
                          onChange(checked);
                        }}
                      />
                    )}
                  />
                </div>
              )}

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="page-status">
                  Status <span className={styles.required}>*</span>
                </label>
                <select id="page-status" className={styles.selectField} {...register("status")}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="page-meta-title">
                  Meta title
                </label>
                <Input
                  id="page-meta-title"
                  {...register("metaTitle")}
                  aria-describedby="page-meta-title-hint"
                />
                <span id="page-meta-title-hint" className={styles.fieldHint}>
                  Leave blank to use the page title
                </span>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="page-meta-description">
                  Meta description
                </label>
                <Input
                  id="page-meta-description"
                  {...register("metaDescription")}
                  aria-describedby="page-meta-description-hint"
                />
                <span id="page-meta-description-hint" className={styles.fieldHint}>
                  Leave blank to use the default site description
                </span>
              </div>

              <div className={`${styles.formField} ${styles.fieldFull}`}>
                <Controller
                  name="content"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RichTextEditor
                      value={value}
                      onChange={onChange}
                      preset="full"
                      headingIds
                      htmlTab
                      label="Content"
                      minHeight={300}
                    />
                  )}
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={() => navigate(ROUTES.cms.pages)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={saving}>
                {saving ? "Creating..." : "Create Page"}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default PageNewPage;
